// FILE: src/components/kyb/sections/TabbedNavigation.tsx
// IMPROVED: Dynamic tab headers with badges and better visual feedback

import React from 'react';
import { Shield, Building2, Users, AlertTriangle } from 'lucide-react';

interface Tab {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  count?: number;
  hasData?: boolean;
}

interface TabbedNavigationProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  officersCount?: number;
  signalsCount?: number;
  hasCompanyData?: boolean;
}

export const TabbedNavigation: React.FC<TabbedNavigationProps> = ({ 
  activeTab, 
  setActiveTab,
  officersCount = 0,
  signalsCount = 0,
  hasCompanyData = false
}) => {
  const tabs: Tab[] = [
    { 
      id: 'overview', 
      label: 'Overview', 
      icon: Shield,
      hasData: true // Overview always has content
    },
    { 
      id: 'company', 
      label: 'Company Details', 
      icon: Building2,
      hasData: hasCompanyData
    },
    { 
      id: 'officers', 
      label: 'Officers', 
      icon: Users,
      count: officersCount,
      hasData: officersCount > 0
    },
    { 
      id: 'signals', 
      label: 'Risk Signals', 
      icon: AlertTriangle,
      count: signalsCount,
      hasData: signalsCount > 0
    },
  ];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-1 mb-6">
      <nav className="flex gap-1">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`
              flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg 
              font-medium transition-all relative group
              ${activeTab === tab.id
                ? 'bg-blue-600 text-white shadow-lg transform scale-[1.02]'
                : tab.hasData 
                  ? 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'
                  : 'text-slate-400 hover:bg-slate-50'
              }
            `}
          >
            <tab.icon className={`
              w-4 h-4 transition-transform
              ${activeTab === tab.id ? 'scale-110' : 'group-hover:scale-105'}
            `} />
            <span className="hidden sm:inline">{tab.label}</span>
            
            {/* Badge for count */}
            {tab.count !== undefined && (
              <span className={`
                ml-1 px-1.5 py-0.5 text-xs rounded-full font-bold
                ${activeTab === tab.id 
                  ? 'bg-white/20 text-white' 
                  : tab.count > 0
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-slate-100 text-slate-400'
                }
              `}>
                {tab.count}
              </span>
            )}
            
            {/* Active indicator */}
            {activeTab === tab.id && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-white/30 rounded-full" />
            )}
          </button>
        ))}
      </nav>
    </div>
  );
};